import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, FolderOpen, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} FCFA`;

/**
 * LOT B — Vue par dossier : regroupe les opérations partageant la même
 * référence client, avec sous-totaux facturé / réglé / reste à payer.
 */
export const DossierGroupedView = ({ company }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openRef, setOpenRef] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const url = company
          ? `${API}/external-reference/dossiers?company=${encodeURIComponent(company)}`
          : `${API}/external-reference/dossiers`;
        const res = await axios.get(url);
        if (alive) setData(res.data);
      } catch {
        if (alive) setData({ enabled: false, dossiers: [] });
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [company]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-slate-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des dossiers…</div>;
  }
  if (!data?.enabled) return null;

  const { dossiers = [], totals = {}, label } = data;

  return (
    <div className="space-y-4" data-testid="dossier-grouped-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { k: 'dossiers', l: 'Dossiers', v: totals.dossiers || 0, money: false },
          { k: 'operations', l: 'Opérations', v: totals.operations || 0, money: false },
          { k: 'total', l: 'Montant total', v: totals.totalAmount, money: true },
          { k: 'remaining', l: 'Reste à payer', v: totals.remainingAmount, money: true },
        ].map((c) => (
          <Card key={c.k} className="border-slate-200">
            <CardContent className="py-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">{c.l}</p>
              <p className={`text-xl font-semibold mt-1 ${c.k === 'remaining' && c.v > 0 ? 'text-amber-600' : 'text-slate-900'}`}
                data-testid={`dossier-total-${c.k}`}>
                {c.money ? fmt(c.v) : c.v}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-[#1db584]" />
            Regroupement par {label}
            <span className="ml-auto text-[11px] font-normal text-slate-400 hidden lg:block">
              montant total · facturé · reste à payer
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {dossiers.length === 0 ? (
            <p className="text-sm text-slate-400 px-6 py-8">Aucun dossier renseigné pour le moment.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {dossiers.map((d) => (
                <div key={d.reference}>
                  <button
                    className="w-full flex items-center gap-3 px-6 py-3 hover:bg-slate-50 text-left transition-colors"
                    onClick={() => setOpenRef(openRef === d.reference ? null : d.reference)}
                    data-testid={`dossier-row-${d.reference}`}
                  >
                    {openRef === d.reference
                      ? <ChevronDown className="w-4 h-4 text-slate-400" />
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="font-mono text-sm text-slate-900 flex-1 truncate">{d.reference}</span>
                    <Badge variant="outline" className="text-xs">{d.operationsCount} op.</Badge>
                    <span className="text-sm text-slate-600 w-32 text-right hidden sm:block" title="Montant total du dossier">{fmt(d.totalAmount)}</span>
                    <span className="text-xs text-slate-400 w-28 text-right hidden lg:block" title="Déjà facturé">{fmt(d.invoicedAmount)}</span>
                    <span className={`text-sm w-32 text-right font-medium ${d.remainingAmount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {fmt(d.remainingAmount)}
                    </span>
                  </button>

                  {openRef === d.reference && (
                    <div className="bg-slate-50/70 px-6 py-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-400 uppercase">
                            <th className="text-left py-2 font-medium">N° Opération</th>
                            <th className="text-left py-2 font-medium">Trajet</th>
                            <th className="text-left py-2 font-medium">Facture</th>
                            <th className="text-right py-2 font-medium">Montant</th>
                            <th className="text-right py-2 font-medium">Reste</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.operations.map((op) => (
                            <tr key={op.id} className="border-t border-slate-200/70">
                              <td className="py-2 font-mono text-slate-700">{op.operationNumber}</td>
                              <td className="py-2 text-slate-500 truncate max-w-[220px]">{op.departure} → {op.arrival}</td>
                              <td className="py-2 text-slate-500">{op.invoiceNumber || '—'}</td>
                              <td className="py-2 text-right text-slate-700">{fmt(op.amount)}</td>
                              <td className={`py-2 text-right ${op.remaining > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                                {op.remaining > 0 ? fmt(op.remaining) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DossierGroupedView;
